import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  Divider,
  Tabs,
  Tab,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Science as ScienceIcon,
  Tune as TuneIcon,
  Storage as StorageIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  AutoFixHigh as AutoFixHighIcon,
  Speed as SpeedIcon,
  FilterList as FilterListIcon,
  HelpOutline as HelpOutlineIcon,
  Psychology as PsychologyIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import API from '../../utils/Axios.utils';

// Quick Inspirations for Non-Tech Recruiters
const QUICK_COPILOT_PROMPTS = [
  {
    title: "Domain / Industry Experience",
    prompt: "Extract the industry or business domain of the candidate's experience (e.g. Healthcare, Fintech, E-commerce, Banking, Automotive, Edtech).",
  },
  {
    title: "Certifications & Credentials",
    prompt: "I want to filter candidates who hold certifications like AWS Solutions Architect, PMP, CISSP, Scrum Master, or GCP.",
  },
  {
    title: "Notice Period (Days)",
    prompt: "Filter candidates by their notice period in number of days (e.g. immediate joiner / 0 days, 15 days, 30 days, 60 days, 90 days).",
  },
  {
    title: "Work Mode Preference",
    prompt: "Extract candidate work mode preference whether they prefer Remote, Hybrid, or Onsite roles.",
  },
  {
    title: "Previous Organizations",
    prompt: "Extract the names of previous organizations or companies the candidate has worked for.",
  },
];

const DATA_TYPES = [
  { value: 'string', label: 'Single Text (string)' },
  { value: 'string_list', label: 'List of Tags (string_list)' },
  { value: 'number', label: 'Numeric Value (number)' },
  { value: 'boolean', label: 'Yes/No Flag (boolean)' },
];

const FILTER_OPS = [
  { value: 'KEYWORD_OR_LIST', label: 'KEYWORD_OR_LIST (Match any in list)' },
  { value: 'ARRAY_CONTAINS', label: 'ARRAY_CONTAINS (Matches tags in array)' },
  { value: 'RANGE_LTE', label: 'RANGE_LTE (Less than or equal to <=)' },
  { value: 'RANGE_GTE', label: 'RANGE_GTE (Greater than or equal to >=)' },
  { value: 'RANGE_BETWEEN', label: 'RANGE_BETWEEN (Within Min and Max)' },
  { value: 'KEYWORD_EXACT', label: 'KEYWORD_EXACT (Exact single match)' },
];

const TARGET_SECTIONS = [
  { value: 'overview', label: 'overview (General resume summary / whole profile)' },
  { value: 'experience', label: 'experience (Work tenure, titles, past companies)' },
  { value: 'skills', label: 'skills (Tech stacks, certifications, tools)' },
  { value: 'education', label: 'education (Degrees, universities, fields)' },
  { value: 'projects', label: 'projects (Project domains, deliverables)' },
];

export default function CvSearchConfigView() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSection, setSelectedSection] = useState('ALL');

  // Modal / Wizard state
  const [modalOpen, setModalOpen] = useState(false);
  const [userPrompt, setUserPrompt] = useState('');
  const [sampleCount, setSampleCount] = useState(3);
  const [proposing, setProposing] = useState(false);
  const [proposedResult, setProposedResult] = useState(null);
  const [saving, setSaving] = useState(false);

  // Inspector Dialog state
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [inspectorProfileId, setInspectorProfileId] = useState('');
  const [inspectorData, setInspectorData] = useState(null);
  const [inspectorLoading, setInspectorLoading] = useState(false);

  // Form state for fine-tuning
  const [formData, setFormData] = useState({
    field_key: '',
    field_label: '',
    target_section: 'experience',
    fallback_section: 'overview',
    data_type: 'string',
    filter_op: 'KEYWORD_OR_LIST',
    extraction_prompt: '',
    description: '',
    is_active: true,
  });

  // Toast Notification
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const config = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  }), [token]);

  // Fetch registered fields
  const fetchFields = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get('/api/candidates/config/fields', config);
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setFields(data);
    } catch (err) {
      console.error('Failed to fetch dynamic fields:', err);
      showToast(err.response?.data?.message || 'Failed to load dynamic fields.', 'error');
    } finally {
      setLoading(false);
    }
  }, [config]);

  // Fetch index stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await API.get('/api/candidates/admin/stats', config);
      const raw = res.data;
      const statsObj = (raw && typeof raw === 'object' && !Array.isArray(raw))
        ? (raw.data || raw)
        : null;
      if (statsObj) {
        setStats(statsObj);
      }
    } catch (err) {
      console.warn('Could not fetch index stats:', err.message);
    }
  }, [config]);

  useEffect(() => {
    fetchFields();
    fetchStats();
  }, [fetchFields, fetchStats]);

  // Toggle active status
  const handleToggleStatus = async (field_key, currentStatus) => {
    const nextStatus = !currentStatus;
    setFields(prev => prev.map(f => f.field_key === field_key ? { ...f, is_active: nextStatus ? 1 : 0 } : f));
    try {
      await API.put(`/api/candidates/config/fields/${field_key}/status`, { is_active: nextStatus }, config);
      showToast(`Field '${field_key}' is now ${nextStatus ? 'active' : 'inactive'}.`, 'success');
    } catch (err) {
      setFields(prev => prev.map(f => f.field_key === field_key ? { ...f, is_active: currentStatus ? 1 : 0 } : f));
      showToast(err.response?.data?.message || 'Failed to update status.', 'error');
    }
  };

  // Trigger in-place backfill
  const handleTriggerBackfill = async (field_key) => {
    try {
      await API.post(`/api/candidates/config/fields/${field_key}/backfill`, {}, config);
      showToast(`In-place backfill started for '${field_key}'.`, 'success');
      fetchFields();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to trigger backfill.', 'error');
    }
  };

  // Delete dynamic field
  const handleDeleteField = async (field_key) => {
    if (!window.confirm(`Are you sure you want to delete dynamic field '${field_key}'?`)) return;
    try {
      await API.delete(`/api/candidates/config/fields/${field_key}`, config);
      showToast(`Field '${field_key}' deleted.`, 'success');
      setFields(prev => prev.filter(f => f.field_key !== field_key));
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete field.', 'error');
    }
  };

  // Step 1: Call Propose API
  const handleProposeField = async () => {
    if (!userPrompt.trim()) {
      showToast('Please describe what you want to filter.', 'warning');
      return;
    }

    setProposing(true);
    setProposedResult(null);
    try {
      const res = await API.post('/api/candidates/config/fields/propose', {
        user_prompt: userPrompt,
        sample_count: Number(sampleCount) || 3,
      }, config);

      const data = res.data;
      if (data && data.proposed_spec) {
        setProposedResult(data);
        setFormData({
          field_key: data.proposed_spec.field_key || '',
          field_label: data.proposed_spec.field_label || '',
          target_section: data.proposed_spec.target_section || 'experience',
          fallback_section: data.proposed_spec.fallback_section || 'overview',
          data_type: data.proposed_spec.data_type || 'string',
          filter_op: data.proposed_spec.filter_op || 'KEYWORD_OR_LIST',
          extraction_prompt: data.proposed_spec.extraction_prompt || '',
          description: data.proposed_spec.description || '',
          is_active: true,
        });
        showToast('AI contract generated & verified on live candidate chunks!', 'success');
      }
    } catch (err) {
      console.error('Propose error:', err);
      showToast(err.response?.data?.message || 'Failed to generate proposal.', 'error');
    } finally {
      setProposing(false);
    }
  };

  // Step 2: Save Dynamic Field
  const handleSaveField = async () => {
    if (!formData.field_key.trim() || !formData.field_label.trim() || !formData.extraction_prompt.trim()) {
      showToast('Please fill in all required fields.', 'warning');
      return;
    }

    setSaving(true);
    try {
      await API.post('/api/candidates/config/fields', formData, config);
      showToast(`Dynamic field '${formData.field_key}' registered & active in search!`, 'success');
      handleCloseModal();
      fetchFields();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create dynamic field.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setUserPrompt('');
    setProposedResult(null);
    setFormData({
      field_key: '',
      field_label: '',
      target_section: 'experience',
      fallback_section: 'overview',
      data_type: 'string',
      filter_op: 'KEYWORD_OR_LIST',
      extraction_prompt: '',
      description: '',
      is_active: true,
    });
  };

  // Inspect profile payload
  const handleOpenInspector = (profileId) => {
    setInspectorProfileId(profileId ? String(profileId) : '1234');
    setInspectorOpen(true);
    if (profileId) {
      loadProfilePayload(profileId);
    }
  };

  const loadProfilePayload = async (profileId) => {
    const id = profileId || inspectorProfileId;
    if (!id) return;
    setInspectorLoading(true);
    setInspectorData(null);
    try {
      const res = await API.get(`/api/candidates/profiles/${id}/payload`, config);
      setInspectorData(res.data);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to fetch profile payload.', 'error');
    } finally {
      setInspectorLoading(false);
    }
  };

  const filteredFields = useMemo(() => {
    return fields.filter(f => {
      const matchesSearch = searchTerm === '' ||
        f.field_label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.field_key?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesSection = selectedSection === 'ALL' || f.target_section === selectedSection;

      return matchesSearch && matchesSection;
    });
  }, [fields, searchTerm, selectedSection]);

  const getSectionBadgeColor = (section) => {
    switch (section) {
      case 'skills': return 'primary';
      case 'experience': return 'success';
      case 'education': return 'info';
      case 'projects': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <TuneIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>
              CV Search & Dynamic Metadata
            </Typography>
          </Stack>
          <Typography variant="body1" sx={{ color: '#64748B', maxWidth: 800 }}>
            Create and manage dynamic resume search dimensions in plain English. AI aligns schema contracts, verifies on real candidate chunks from Qdrant, and updates vector facets with zero re-embeddings.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => handleOpenInspector(null)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Inspect Vector Payload
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchFields(); fetchStats(); }}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AutoFixHighIcon />}
            onClick={() => setModalOpen(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              bgcolor: '#4F46E5',
              boxShadow: '0 4px 14px rgba(79, 70, 229, 0.35)',
              '&:hover': { bgcolor: '#4338CA' },
            }}
          >
            Add Filter with AI
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase' }}>
                    Active Dynamic Fields
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', mt: 0.5 }}>
                    {fields.filter(f => f.is_active).length} / {fields.length}
                  </Typography>
                </Box>
                <FilterListIcon sx={{ fontSize: 36, color: '#6366F1' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase' }}>
                    Vector Engine Status
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#10B981', mt: 0.5 }}>
                    Online & Ready
                  </Typography>
                </Box>
                <StorageIcon sx={{ fontSize: 36, color: '#10B981' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase' }}>
                    Total Indexed Profiles
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', mt: 0.5 }}>
                    {stats?.indexed_profiles ?? stats?.indexed ?? '—'}
                  </Typography>
                </Box>
                <SpeedIcon sx={{ fontSize: 36, color: '#F59E0B' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase' }}>
                    Zero Re-Embedding
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#6366F1', mt: 0.5 }}>
                    In-Place Updates
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 36, color: '#6366F1' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table Container */}
      <Paper sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, bgcolor: '#FFFFFF' }}>
          <TextField
            size="small"
            placeholder="Search dynamic fields..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />

          <Tabs
            value={selectedSection}
            onChange={(e, val) => setSelectedSection(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, px: 1.5, fontSize: '0.8125rem' } }}
          >
            <Tab label="All Sections" value="ALL" />
            <Tab label="Experience" value="experience" />
            <Tab label="Skills" value="skills" />
            <Tab label="Education" value="education" />
          </Tabs>
        </Box>

        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading dynamic metadata schema...
            </Typography>
          </Box>
        ) : filteredFields.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <PsychologyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No dynamic metadata fields configured.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click "Add Filter with AI" to generate a zero-mismatch field from natural language.
            </Typography>
            <Button variant="contained" startIcon={<AutoFixHighIcon />} onClick={() => setModalOpen(true)}>
              Add Filter with AI
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 800 }}>
              <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Field Label & Key</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Target Section</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Data Type & Filter Operator</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Search Query Description</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Active</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Backfill</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFields.map((field) => (
                  <TableRow key={field.field_key} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {field.field_label}
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'primary.main', bgcolor: '#EEF2FF', px: 0.75, py: 0.25, borderRadius: 1 }}>
                        facets.{field.field_key}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={field.target_section?.toUpperCase()}
                        size="small"
                        color={getSectionBadgeColor(field.target_section)}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                      />
                    </TableCell>

                    <TableCell>
                      <Stack direction="column" spacing={0.5} alignItems="flex-start">
                        <Chip
                          label={field.data_type}
                          size="small"
                          sx={{ fontSize: '0.7rem', bgcolor: '#F1F5F9', fontWeight: 600 }}
                        />
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748B' }}>
                          {field.filter_op}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {field.description || field.extraction_prompt}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Switch
                        size="small"
                        checked={Boolean(field.is_active)}
                        onChange={() => handleToggleStatus(field.field_key, field.is_active)}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={field.backfill_status || 'PENDING'}
                        size="small"
                        color={field.backfill_status === 'COMPLETED' ? 'success' : field.backfill_status === 'RUNNING' ? 'warning' : 'default'}
                        sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                      />
                      {field.total_processed > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
                          {field.total_processed} chunks
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title="Trigger In-Place Backfill">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleTriggerBackfill(field.field_key)}
                          sx={{ mr: 0.5 }}
                        >
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Field">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteField(field.field_key)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* AI Schema Realignment Copilot Dialog */}
      <Dialog
        open={modalOpen}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') handleCloseModal();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <AutoFixHighIcon sx={{ color: '#4F46E5', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
                AI Schema Realignment Copilot
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B' }}>
                Deconstructs recruiter demands into zero-mismatch vector facets and tests on real Qdrant chunks.
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={handleCloseModal}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 3, maxHeight: 'calc(90vh - 140px)', overflowY: 'auto' }}>
          {/* Step 1: Prompt Input */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1E293B' }}>
            1. Describe what you want to filter in plain English:
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="e.g. Extract the domain or industry of the candidate's work experience (e.g. Healthcare, Fintech, Banking, Automotive)."
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
            disabled={proposing}
            sx={{ mb: 2 }}
          />

          {/* Prompt Inspiration Chips */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block', mb: 1 }}>
              Quick Templates:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {QUICK_COPILOT_PROMPTS.map((item, idx) => (
                <Chip
                  key={idx}
                  label={item.title}
                  size="small"
                  onClick={() => setUserPrompt(item.prompt)}
                  sx={{ cursor: 'pointer', mb: 0.5, bgcolor: '#F1F5F9', '&:hover': { bgcolor: '#E2E8F0' } }}
                />
              ))}
            </Stack>
          </Box>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <TextField
              type="number"
              size="small"
              label="Live Sample Count"
              value={sampleCount}
              onChange={(e) => setSampleCount(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
              disabled={proposing}
              sx={{ width: 160 }}
              inputProps={{ min: 1, max: 10 }}
            />
            <Button
              variant="contained"
              startIcon={proposing ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
              onClick={handleProposeField}
              disabled={proposing || !userPrompt.trim()}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: '#4F46E5',
                '&:hover': { bgcolor: '#4338CA' },
              }}
            >
              {proposing ? 'Analyzing & Testing Samples...' : 'Generate AI Specification & Test on Samples'}
            </Button>
          </Stack>

          {/* Step 2: Proposed Result & Dry-Run Preview */}
          {proposedResult && proposedResult.proposed_spec && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 3 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#10B981', fontSize: 20 }} />
                2. Generated Zero-Mismatch Contract & Live Sandbox Verification:
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Field Key (Qdrant Payload)"
                    value={formData.field_key}
                    onChange={(e) => setFormData({ ...formData, field_key: e.target.value })}
                    helperText={`Stored in Qdrant as: facets.${formData.field_key}`}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Field Label (UI Display)"
                    value={formData.field_label}
                    onChange={(e) => setFormData({ ...formData, field_label: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Target Section</InputLabel>
                    <Select
                      value={formData.target_section}
                      label="Target Section"
                      onChange={(e) => setFormData({ ...formData, target_section: e.target.value })}
                    >
                      {TARGET_SECTIONS.map(s => (
                        <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Data Type</InputLabel>
                    <Select
                      value={formData.data_type}
                      label="Data Type"
                      onChange={(e) => setFormData({ ...formData, data_type: e.target.value })}
                    >
                      {DATA_TYPES.map(t => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filter Operator</InputLabel>
                    <Select
                      value={formData.filter_op}
                      label="Filter Operator"
                      onChange={(e) => setFormData({ ...formData, filter_op: e.target.value })}
                    >
                      {FILTER_OPS.map(op => (
                        <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    label="Extraction Prompt (LLM Extraction)"
                    value={formData.extraction_prompt}
                    onChange={(e) => setFormData({ ...formData, extraction_prompt: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Query Parser Intent Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    helperText="Tells Query Intent Parser when user search queries relate to this dynamic dimension."
                  />
                </Grid>
              </Grid>

              {/* Sandbox Dry Run Output */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1E293B' }}>
                Live Sandbox Extraction on Real Qdrant Chunks ({proposedResult.sample_extractions?.length || 0} samples tested):
              </Typography>

              <Stack spacing={1.5} sx={{ mb: 2 }}>
                {proposedResult.sample_extractions?.map((s, idx) => (
                  <Paper key={idx} sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        Candidate: {s.candidate_name || `Profile #${s.profile_id}`} (Section: {s.section_type || 'overview'})
                      </Typography>
                      <Chip
                        label={s.status === 'success' ? 'Extracted Successfully' : 'No Match'}
                        size="small"
                        color={s.status === 'success' ? 'success' : 'default'}
                        sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                      />
                    </Stack>

                    <Typography variant="caption" sx={{ display: 'block', color: '#64748B', fontStyle: 'italic', mb: 1, bgcolor: '#FFFFFF', p: 1, borderRadius: 1, border: '1px solid #E2E8F0' }}>
                      "{s.snippet}"
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#4F46E5' }}>
                        facets.{formData.field_key}:
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: '#EEF2FF', px: 1, py: 0.25, borderRadius: 1, color: '#3730A3', fontWeight: 600 }}>
                        {JSON.stringify(s.extracted_value)}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleCloseModal} sx={{ textTransform: 'none', color: '#64748B' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveField}
            disabled={saving || !proposedResult || !formData.field_key.trim()}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: '#4F46E5',
              '&:hover': { bgcolor: '#4338CA' },
            }}
          >
            {saving ? 'Registering Field...' : 'Approve & Register Field'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vector Payload Inspector Dialog */}
      <Dialog
        open={inspectorOpen}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') setInspectorOpen(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <VisibilityIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Live Qdrant Vector Payload Inspector
            </Typography>
          </Stack>
          <IconButton size="small" onClick={() => setInspectorOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2.5, maxHeight: 'calc(90vh - 140px)', overflowY: 'auto' }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <TextField
              size="small"
              label="Candidate Profile ID"
              placeholder="e.g. 1234 or 1191"
              value={inspectorProfileId}
              onChange={(e) => setInspectorProfileId(e.target.value)}
              sx={{ width: 220 }}
            />
            <Button
              variant="contained"
              startIcon={inspectorLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={() => loadProfilePayload(inspectorProfileId)}
              disabled={inspectorLoading || !inspectorProfileId.trim()}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Fetch Live Payload
            </Button>
          </Stack>

          {inspectorLoading && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Retrieving point payloads from Qdrant...
              </Typography>
            </Box>
          )}

          {inspectorData && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Candidate: {inspectorData.candidate_name} (#{inspectorData.profile_id}) — {inspectorData.total_chunks} Chunks
              </Typography>

              <Typography variant="caption" sx={{ fontWeight: 700, color: '#4F46E5', display: 'block', mb: 0.5 }}>
                Active Vector Facets (Zero Re-Embedding):
              </Typography>
              <Paper sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 2, mb: 2 }}>
                <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.8125rem', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(inspectorData.facets, null, 2)}
                </pre>
              </Paper>

              <Accordion defaultExpanded sx={{ border: '1px solid #E2E8F0', borderRadius: 2, boxShadow: 'none' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Raw Chunk Payloads ({inspectorData.chunks?.length || 0} chunks)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.75rem', maxHeight: 300, overflow: 'auto' }}>
                    {JSON.stringify(inspectorData.chunks, null, 2)}
                  </pre>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Toast Snackbar */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

